import geolocation from "./geolocation";
import locationsModal from "./locations-modal";

const severityOrder = ["danger", "alert", "attention"];
const rotationIntervalMs = 6000;
const defaultLocationFallback = { name: "São Paulo", uf: "SP" };
const mobileBreakpoint = 991;
const cityWidthLockDelayMs = 4500;
const cityCollapseDelayMs = 5000;
const marqueeSpeedDivisor = 30;
const marqueeMinDuration = 6;
const marqueeMaxDuration = 20;
const marqueeTrailingPadding = 32;
let alertRotationInterval = null;
let resizeTimeoutId = null;
let cityLinkWidthTimeoutId = null;
let cityLinkCollapseTimeoutId = null;
let lastCityLinkIsMobile = null;

const clearCityLinkTimeouts = () => {
  if (cityLinkWidthTimeoutId) {
    clearTimeout(cityLinkWidthTimeoutId);
    cityLinkWidthTimeoutId = null;
  }

  if (cityLinkCollapseTimeoutId) {
    clearTimeout(cityLinkCollapseTimeoutId);
    cityLinkCollapseTimeoutId = null;
  }
};

const resetCityLinkStyles = (cityLink) => {
  cityLink.css('width', '');
  cityLink.css('margin', '');
  cityLink.css('overflow', '');
};

const applyAlertMarquee = () => {
  const containers = $('.header-bar-alert-text');

  containers.each((_, element) => {
    const containerEl = $(element);
    const contentEl = containerEl.find('.header-bar-alert-text-content').first();
    const domEl = contentEl[0];
    if (!domEl) return;

    contentEl.removeClass('is-marquee');
    contentEl.css('--marquee-distance', '');
    contentEl.css('--marquee-after-width', '');
    contentEl.css('--marquee-duration', '');
    contentEl.attr('data-marquee-active', 'false');
    contentEl.attr('data-marquee-clone', '');

    const parentAlert = containerEl.closest('.header-bar-alert');
    const visibilityFlag = parentAlert.attr('data-alert-visible');
    let isAlertVisible = visibilityFlag === 'true';

    if (visibilityFlag === undefined) {
      const rotator = containerEl.closest('[data-alert-rotator]');
      if (parentAlert.length && rotator.length) {
        const alertRect = parentAlert[0].getBoundingClientRect();
        const rotatorRect = rotator[0].getBoundingClientRect();
        isAlertVisible = alertRect.bottom > rotatorRect.top && alertRect.top < rotatorRect.bottom;
      }
    }

    if (!isAlertVisible) {
      return;
    }

    const scrollWidth = domEl.scrollWidth;
    const clientWidth = containerEl[0]?.clientWidth || domEl.clientWidth;

    if (!clientWidth || scrollWidth <= clientWidth + 1) {
      return;
    }

    const distance = scrollWidth + marqueeTrailingPadding;
    const duration = Math.min(
      Math.max(distance / marqueeSpeedDivisor, marqueeMinDuration),
      marqueeMaxDuration,
    );

    contentEl.attr('data-marquee-clone', domEl.textContent.trim());
    contentEl.css('--marquee-distance', `${distance}px`);
    contentEl.css('--marquee-after-width', `${clientWidth}px`);
    contentEl.css('--marquee-duration', `${duration}s`);

    // Force reflow to restart animation when toggling the class.
    void domEl.offsetWidth;

    contentEl.addClass('is-marquee');
    contentEl.attr('data-marquee-active', 'true');
  });
};

const toStringOrEmpty = (value) => (value == null ? '' : `${value}`);

const getSeverityRank = (alertType) => {
  const index = severityOrder.indexOf(alertType);
  return index === -1 ? severityOrder.length : index;
};

const formatSequenceId = (value) => value.toString().padStart(2, '0');

const getIsLocationGranted = async () => {
  const permissionStatus = await navigator.permissions.query({ name: "geolocation" });

  return permissionStatus.state === "granted";
};

const handleCurrentWeatherLink = async () => {
  const isLocationGranted = await getIsLocationGranted();

  if (isLocationGranted) return;

  $('#current-weather-link').attr("role", "button");
  $('#current-weather-link')[0].dataset.id = "Bar_Highlights_Link_UseLocation";

  $('#current-weather-link')[0].onclick = (e) => {
    e.preventDefault();

    locationsModal.open();
  };
};

const shouldCollapseCityLink = () => {
  const adSlot = $("#HeaderMenu_AdUnit");
  const hasAdSlot = adSlot.length > 0;

  if (!hasAdSlot) {
    return true;
  }

  const isAdFilled = adSlot.children().length > 0;

  return isAdFilled;
};

const collapseCityLink = (cityLink) => {
  cityLink.css('width', '0');
  cityLink.css('margin', '0');
  cityLink.css('overflow', 'hidden');
  cityLink.trigger('city:collapsed');
};

const handleCityLinkStyle = (force = false) => {
  const cityLink = $("#current-weather-city-header");

  if (!cityLink.length) return;

  const isMobileWidth = window.innerWidth <= mobileBreakpoint;

  if (!force && lastCityLinkIsMobile === isMobileWidth) {
    return;
  }

  clearCityLinkTimeouts();
  resetCityLinkStyles(cityLink);

  if (!isMobileWidth) {
    lastCityLinkIsMobile = isMobileWidth;
    cityLink.trigger('city:reset');
    return;
  }

  cityLinkWidthTimeoutId = setTimeout(() => {
    const currentWidth = cityLink.outerWidth();
    if (currentWidth > 0) {
      cityLink.css('width', `${currentWidth}px`);
    }

    applyAlertMarquee();
  }, cityWidthLockDelayMs);

  cityLinkCollapseTimeoutId = setTimeout(() => {
    if (!shouldCollapseCityLink()) {
      resetCityLinkStyles(cityLink);
      applyAlertMarquee();
      cityLink.trigger('city:reset');
      return;
    }

    collapseCityLink(cityLink);
  }, cityCollapseDelayMs);

  lastCityLinkIsMobile = isMobileWidth;
  applyAlertMarquee();

  cityLink.off('city:collapsed.marquee city:reset.marquee');

  cityLink.on('city:collapsed.marquee city:reset.marquee', () => {
    applyAlertMarquee();
  });
};

const stateByAcronym = {
  "AC": "Acre",
  "AL": "Alagoas",
  "AP": "Amapá",
  "AM": "Amazonas",
  "BA": "Bahia",
  "CE": "Ceará",
  "DF": "Distrito Federal",
  "ES": "Espírito Santo",
  "GO": "Goiás",
  "MA": "Maranhão",
  "MT": "Mato Grosso",
  "MS": "Mato Grosso do Sul",
  "MG": "Minas Gerais",
  "PA": "Pará",
  "PB": "Paraíba",
  "PR": "Paraná",
  "PE": "Pernambuco",
  "PI": "Piauí",
  "RJ": "Rio de Janeiro",
  "RN": "Rio Grande do Norte",
  "RS": "Rio Grande do Sul",
  "RO": "Rondônia",
  "RR": "Roraima",
  "SC": "Santa Catarina",
  "SP": "São Paulo",
  "SE": "Sergipe",
  "TO": "Tocantins",
};

const getLocationState = async () => {
  try {
    const position = await geolocation.getUserLocation();

    const formData = new URLSearchParams();
    formData.append('latitude', position.coords.latitude.toFixed(5));
    formData.append('longitude', position.coords.longitude.toFixed(5));

    const response = await fetch('/json/minha-localizacao', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();
    const locationUf = result?.data?.[0]?.uf;

    if (!locationUf) {
      return defaultLocationFallback;
    }

    const formattedName = stateByAcronym[locationUf];

    if (!formattedName) {
      return defaultLocationFallback;
    }

    return { name: formattedName, uf: locationUf };
  } catch (error) {
    console.error("Error fetching location data:", error);
    return defaultLocationFallback;
  }
};

const getAlerts = async () => {
  try {
    const response = await fetch('/json/newest-alert');

    const result = await response.json();

    return Array.isArray(result) ? result : [];
  } catch (error) {
    console.error("Error fetching alerts:", error);
    return [];
  }
};

const getAlertsForMyLocation = async () => {
  const [location, alerts] = await Promise.all([getLocationState(), getAlerts()]);

  if (!location || !alerts.length) {
    return { location, alerts: [] };
  }

  const filteredAlerts = alerts.filter((alert) => Array.isArray(alert?.tags) && alert.tags.some((tag) => tag?.name === location.name));

  return { location, alerts: filteredAlerts };
};

const getAlertContainer = () => {
  const rotator = $('[data-alert-rotator]');
  if (!rotator.length) return null;

  const track = rotator.find('[data-alert-track]');
  if (!track.length) return null;

  return { rotator, track };
};

const createAlertContainer = () => {
  const headerBar = $('.header-bar');
  if (!headerBar.length) return null;

  headerBar.find('.header-bar-ad').remove();

  const rotator = $('<div class="header-bar-alerts" data-alert-rotator></div>');
  const track = $('<div class="header-bar-alerts-track" data-alert-track></div>');

  rotator.append(track);
  headerBar.prepend(rotator);

  return { rotator, track };
};

const createAlertElement = (alert, fallbackStateUf, sequenceIndex) => {
  const alertLink = $('<a class="header-bar-alert" data-id="Bar_Highlights_Alert_Text" data-alert="true"></a>');

  if (typeof sequenceIndex === 'number' && Number.isFinite(sequenceIndex)) {
    const formattedIndex = formatSequenceId(sequenceIndex);
    alertLink.attr('data-id', `Bar_Highlights_Alert_Text-${formattedIndex}`);
  }

  const alertId = toStringOrEmpty(alert?.id);
  const alertSlug = toStringOrEmpty(alert?.slug);
  const alertType = toStringOrEmpty(alert?.alertType);
  const stateUf = toStringOrEmpty(alert?.stateUf ?? fallbackStateUf);

  alertLink.attr('data-alert-id', alertId);
  alertLink.attr('href', `/alerta/${alertSlug}`);
  alertLink.attr('data-alert-slug', alertSlug);
  alertLink.attr('data-alert-type', alertType);
  alertLink.attr('data-alert-state', stateUf);

  const icon = $('<img src="/dist/images/v2/svg/alert.svg" alt="Ícone do alerta">');
  const text = $('<span class="header-bar-alert-text"></span>');
  const textContent = $('<span class="header-bar-alert-text-content"></span>').text(alert?.alertTitle || '');
  text.append(textContent);

  alertLink.append(icon);
  alertLink.append(text);

  return alertLink;
};

const appendAlertsToTrack = (alerts, stateUf) => {
  if (!alerts.length) return;

  const existingContainer = getAlertContainer();
  const existingSlugs = new Set();
  const existingIds = new Set();

  if (existingContainer) {
    existingContainer.track.find('.header-bar-alert').each((_, element) => {
      const alertElement = $(element);
      const slug = toStringOrEmpty(alertElement.data('alertSlug')).trim();
      const id = toStringOrEmpty(alertElement.data('alertId')).trim();

      if (slug) existingSlugs.add(slug);
      if (id) existingIds.add(id);
    });
  }

  const sortedAlerts = [...alerts].sort((a, b) => getSeverityRank(a?.alertType) - getSeverityRank(b?.alertType));
  const batchSlugs = new Set();
  const batchIds = new Set();

  const alertsToAppend = sortedAlerts.filter((alert) => {
    const slug = toStringOrEmpty(alert?.slug).trim();
    const id = toStringOrEmpty(alert?.id).trim();

    if (slug) {
      if (existingSlugs.has(slug) || batchSlugs.has(slug)) {
        return false;
      }
    }

    if (id) {
      if (existingIds.has(id) || batchIds.has(id)) {
        return false;
      }
    }

    if (slug) batchSlugs.add(slug);
    if (id) batchIds.add(id);

    return true;
  });

  if (!alertsToAppend.length) {
    return;
  }

  const container = existingContainer || createAlertContainer();
  if (!container) return;

  const { track } = container;
  const existingCount = track.find('.header-bar-alert').length;
  let nextSequenceIndex = existingCount + 1;

  alertsToAppend.forEach((alert) => {
    const alertElement = createAlertElement(alert, stateUf, nextSequenceIndex);
    track.append(alertElement);
    const slug = toStringOrEmpty(alert?.slug).trim();
    const id = toStringOrEmpty(alert?.id).trim();

    if (slug) {
      existingSlugs.add(slug);
    }

    if (id) {
      existingIds.add(id);
    }

    nextSequenceIndex += 1;
  });

  applyAlertMarquee();
};

const updateWrapperSeverity = () => {
  const wrapper = $('.header-bar-wrapper');
  if (!wrapper.length) return;

  const alertTypes = [];

  $('[data-alert-track] .header-bar-alert').each((_, element) => {
    const type = $(element).data('alertType');
    if (type && !alertTypes.includes(type)) {
      alertTypes.push(type);
    }
  });

  if (!alertTypes.length) return;

  alertTypes.sort((a, b) => getSeverityRank(a) - getSeverityRank(b));
  const highestSeverity = alertTypes[0];

  severityOrder.forEach((type) => wrapper.removeClass(type));

  if (highestSeverity) {
    wrapper.addClass(highestSeverity);
  }
};

const initAlertRotator = () => {
  const container = getAlertContainer();

  if (alertRotationInterval) {
    clearInterval(alertRotationInterval);
    alertRotationInterval = null;
  }

  if (!container) {
    return;
  }

  const { rotator, track } = container;
  const alerts = track.find('.header-bar-alert');

  alerts.attr('data-alert-visible', 'false');

  if (!alerts.length) {
    rotator.css({ height: '' });
    track.css({ transform: '' });
    applyAlertMarquee();
    return;
  }

  const heights = alerts.map((_, element) => $(element).outerHeight(true)).get();
  const offsets = heights.reduce((acc, height, index) => {
    if (index === 0) {
      acc.push(0);
    } else {
      acc.push(acc[index - 1] + heights[index - 1]);
    }
    return acc;
  }, []);

  let currentIndex = 0;

  const applyPosition = (index) => {
    const offset = offsets[index] || 0;
    const height = heights[index] || 0;

    rotator.css('height', `${height}px`);
    track.css('transform', `translateY(-${offset}px)`);

    alerts.attr('data-alert-visible', 'false');
    const visibleAlert = alerts.eq(index);
    visibleAlert.attr('data-alert-visible', 'true');

    applyAlertMarquee();
  };

  track.css('transition', 'transform 0.5s ease-in-out');
  applyPosition(currentIndex);

  if (alerts.length <= 1) {
    return;
  }

  alertRotationInterval = setInterval(() => {
    currentIndex = (currentIndex + 1) % alerts.length;
    applyPosition(currentIndex);
  }, rotationIntervalMs);
};

const scheduleRotatorRefresh = () => {
  if (resizeTimeoutId) {
    clearTimeout(resizeTimeoutId);
  }

  resizeTimeoutId = setTimeout(() => {
    handleCityLinkStyle(true);
    initAlertRotator();
    applyAlertMarquee();
  }, 300);
};

const enhanceAlertsBar = async () => {
  const pathname = window.location.pathname;
  const isForecastPage = pathname.includes('/previsao-do-tempo/');
  const pageState = ($('.header-bar-wrapper').data('pageState') || '').toString().toUpperCase();

  const { location, alerts } = await getAlertsForMyLocation();

  if (!alerts.length) {
    initAlertRotator();
    return;
  }

  const locationUf = (location?.uf || '').toUpperCase();
  const shouldAppendLocationAlerts = () => {
    if (!locationUf) return false;
    if (!isForecastPage) return true;
    if (!pageState) return true;

    return pageState !== locationUf;
  };

  if (!shouldAppendLocationAlerts()) {
    initAlertRotator();
    return;
  }

  appendAlertsToTrack(alerts, locationUf);
  updateWrapperSeverity();
  initAlertRotator();
};

const init = () => {
  handleCityLinkStyle();
  handleCurrentWeatherLink();
  updateWrapperSeverity();
  initAlertRotator();
  enhanceAlertsBar();

  window.addEventListener('resize', scheduleRotatorRefresh);
};

const headerInfoBar = {
  init,
};

export default headerInfoBar;
